import { normalize } from './normalize.js';
/**
 * The two-pass tile evaluation (spec §3), the heart of the game.
 *
 * The invariant the whole thing exists to protect: **a repeated letter in the
 * guess never earns more `exact` + `present` marks than that letter's frequency
 * in the target.** A single pass cannot do this — it would paint every copy of a
 * letter yellow regardless of how many the target holds — so evaluation runs in
 * two passes over a decrementing pool of the target's letters:
 *
 *  1. **Exact (green):** every position where guess and target agree is `exact`,
 *     and one of that letter is spent from the pool. Greens are claimed first so
 *     a later duplicate can never steal the count a correct position is owed.
 *  2. **Present / absent (yellow / grey):** the remaining tiles, left to right —
 *     `present` while the pool still holds that letter (spending one), else
 *     `absent`.
 *
 * Guess and target are normalized here too, so a caller passing raw input still
 * gets a correct grid; callers that already normalized pay only an idempotent
 * second pass.
 */
export function evaluate(guess, target) {
    const g = normalize(guess);
    const t = normalize(target);
    const result = Array.from({ length: g.length }, () => 'absent');
    // The pool of still-unmatched target letters.
    const pool = new Map();
    for (const ch of t)
        pool.set(ch, (pool.get(ch) ?? 0) + 1);
    // Pass 1 — greens, spending from the pool so duplicates can't over-count.
    for (let i = 0; i < g.length; i += 1) {
        if (g[i] === t[i]) {
            result[i] = 'exact';
            pool.set(g[i], (pool.get(g[i]) ?? 0) - 1);
        }
    }
    // Pass 2 — yellows where the pool still has the letter, else grey.
    for (let i = 0; i < g.length; i += 1) {
        if (result[i] === 'exact')
            continue;
        const ch = g[i];
        const left = pool.get(ch) ?? 0;
        if (left > 0) {
            result[i] = 'present';
            pool.set(ch, left - 1);
        }
    }
    return result;
}
//# sourceMappingURL=evaluate.js.map